import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Chatbot = () => {
  const { i18n } = useTranslation();
  const isTe = i18n.language === 'te';

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);
  const [suggestedPlans, setSuggestedPlans] = useState(null);
  const messagesEndRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          sender: 'bot',
          text: isTe
            ? 'నమస్కారం! నేను కవితను, మీ బీమా సహాయకురాలిని. మీ కుటుంబానికి తగిన ఉత్తమ బీమా ప్లాన్‌ను ఎంచుకోవడంలో మీకు సహాయం చేయడానికి ఇక్కడ ఉన్నాను. మీ పేరు తెలుసుకోవచ్చా?'
            : 'Hello! I am Kavitha, your insurance advisor assistant. I am here to help you find the best insurance plan for your needs. May I know your name?',
        },
      ]);
    }
  }, [isOpen, messages.length, isTe]);

  const handleOpenChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMsg = inputMessage.trim();
    setMessages((prev) => [...prev, { sender: 'user', text: userMsg }]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chatbot/chat`, {
        message: userMsg,
        user_data: Object.keys(userData).length > 0 ? userData : null,
      });

      const botResponse = response.data.response;
      setMessages((prev) => [...prev, { sender: 'bot', text: botResponse }]);

      if (response.data.suggested_plans && response.data.suggested_plans.length > 0) {
        setSuggestedPlans(response.data.suggested_plans);
      }
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: isTe
            ? 'క్షమించండి, సర్వర్ స్పందించడం లేదు. దయచేసి సీనియర్ సలహాదారు వి. ఎస్. రెడ్డి గారిని +91-9849664800 లో నేరుగా సంప్రదించండి.'
            : 'I apologize for the inconvenience. Please call V. S. Reddy directly at +91-9849664800 for immediate assistance.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendDetails = async (plan) => {
    try {
      await axios.post(`${BACKEND_URL}/api/chatbot/send-details`, {
        user_name: userData.name || 'Anonymous',
        age: userData.age || 30,
        gender: userData.gender || 'not specified',
        phone: userData.phone || 'not provided',
        relationship: userData.relationship || 'self',
        plan_name: plan.name,
        plan_id: plan.id,
      });

      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: isTe
            ? `${plan.nameTe || plan.name} పై ఆసక్తి చూపినందుకు ధన్యవాదాలు! వి. ఎస్. రెడ్డి గారు త్వరలోనే మిమ్మల్ని సంప్రదిస్తారు. లేదా నేరుగా +91-9849664800 కు కాల్ చేయవచ్చు.`
            : `Thank you for your interest in ${plan.name}! V.S. Reddy will contact you soon at your provided number. You can also call directly at +91-9849664800.`,
        },
      ]);
    } catch (error) {
      console.error('Send details error:', error);
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: isTe
            ? 'ప్రస్తుతం వివరాలు పంపడం సాధ్యం కాలేదు. దయచేసి నేరుగా +91-9849664800 కు కాల్ చేయండి.'
            : 'Unable to send details at the moment. Please call +91-9849664800 directly.',
        },
      ]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div>
      <button
        onClick={handleOpenChat}
        className="fixed bottom-6 right-6 bg-[#003876] text-white p-4 rounded-full shadow-lg hover:bg-[#002856] transition-all hover:scale-110 z-50"
        style={{ display: isOpen ? 'none' : 'block' }}
        aria-label={isTe ? 'చాట్ ప్రారంభించండి' : 'Open chat'}
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      <div 
        className="fixed bottom-6 right-6 w-96 h-[600px] bg-white dark:bg-slate-900 rounded-lg shadow-2xl z-50 border border-gray-200 dark:border-slate-800"
        style={{ display: isOpen ? 'flex' : 'none', flexDirection: 'column' }}
      >
        <div className="bg-[#003876] text-white p-4 rounded-t-lg flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg">{isTe ? 'కవితతో మాట్లాడండి' : 'Chat with Kavitha'}</h3>
            <p className="text-xs text-gray-200">{isTe ? 'మీ బీమా సహాయకురాలు' : 'Your Insurance Advisor'}</p>
          </div>
          <button
            onClick={handleOpenChat}
            className="hover:bg-[#002856] p-1 rounded"
            aria-label="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-2 rounded-lg ${
                  msg.sender === 'user'
                    ? 'bg-[#003876] text-white'
                    : 'bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-gray-200'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
              </div>
            </div>
          ))}

          {suggestedPlans && suggestedPlans.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {isTe ? 'సిఫార్సు చేయబడిన ప్లాన్లు:' : 'Recommended Plans:'}
              </p>
              {suggestedPlans.slice(0, 3).map((plan) => (
                <div
                  key={plan.id}
                  className="bg-gray-50 dark:bg-slate-800/60 p-3 rounded border border-gray-200 dark:border-slate-700"
                >
                  <p className="font-semibold text-sm text-[#003876] dark:text-sky-400">
                    {isTe ? plan.nameTe || plan.name : plan.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {isTe ? plan.shortDescTe || plan.short_description : plan.short_description}
                  </p>
                  <button
                    onClick={() => handleSendDetails(plan)}
                    className="mt-2 text-xs bg-[#FFD700] text-[#003876] px-3 py-1 rounded font-semibold hover:bg-[#FFC700] transition-colors"
                  >
                    {isTe ? 'వివరాలు పంపండి' : 'Send Details'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 dark:bg-slate-800 px-4 py-2 rounded-lg">
                <p className="text-sm text-gray-500 animate-pulse">
                  {isTe ? 'టైప్ చేస్తోంది...' : 'Typing...'}
                </p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-slate-800">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isTe ? 'సందేశాన్ని టైప్ చేయండి...' : 'Type your message...'}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:outline-none focus:border-[#003876] text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              disabled={loading}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !inputMessage.trim()}
              className="bg-[#003876] text-white p-2 rounded-lg hover:bg-[#002856] disabled:opacity-50 transition-colors"
              aria-label="Send message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
